// src/components/ui/pageTheme.js — single source for page layout, typography, and colors

import { COLORS, STATUS_HEX } from '../../constants';
import { UI_FONT } from './theme';

const HEADING = '#0f172a';
const MUTED = '#64748b';

/** Shared layout tokens for all main app pages (aligns with nav + dashboard). */
export const PAGE = {
  fontFamily: UI_FONT,
  maxWidth: 1600,
  navHeight: 64,
  paddingX: 24,
  paddingY: 24,
  gap: 16,
  sectionGap: 20,

  colors: {
    bg: '#f3f6fb',
    surface: COLORS.surface,
    heading: HEADING,
    text: HEADING,
    body: HEADING,
    muted: MUTED,
    line: '#e2e8f0',
    border: '#dbe3ef',
    tableHead: '#f8fafc',
    tableStripe: '#f8fafc',
    success: STATUS_HEX.success,
    danger: STATUS_HEX.danger,
    warning: STATUS_HEX.warning,
    slate: STATUS_HEX.slate,
    successSoft: '#f0fdf4',
    dangerSoft: '#fef2f2',
    warningSoft: '#fffbeb',
    infoSoft: '#eff6ff',
    info: '#2563eb',
  },

  radius: {
    card: 16,
    input: 12,
  },

  type: {
    pageTitle: {
      margin: 0,
      fontSize: 26,
      fontWeight: 800,
      fontFamily: UI_FONT,
      color: HEADING,
      letterSpacing: '-0.3px',
      lineHeight: 1.2,
    },
    pageSubtitle: {
      margin: '6px 0 0',
      fontSize: 14,
      fontWeight: 500,
      fontFamily: UI_FONT,
      color: MUTED,
      lineHeight: 1.4,
    },
    sectionTitle: {
      margin: 0,
      fontSize: 20,
      fontWeight: 800,
      fontFamily: UI_FONT,
      color: HEADING,
      lineHeight: 1.3,
    },
    sectionSubtitle: {
      margin: '6px 0 0',
      fontSize: 14,
      fontWeight: 500,
      fontFamily: UI_FONT,
      color: MUTED,
      lineHeight: 1.4,
    },
    subsectionTitle: {
      margin: '0 0 16px',
      fontSize: 16,
      fontWeight: 800,
      fontFamily: UI_FONT,
      color: HEADING,
      lineHeight: 1.3,
    },
    cardLabel: {
      fontSize: 13,
      fontWeight: 700,
      fontFamily: UI_FONT,
      color: MUTED,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    metricValue: {
      fontSize: 20,
      fontWeight: 800,
      fontFamily: UI_FONT,
      lineHeight: 1,
    },
    label: {
      fontSize: 12,
      fontWeight: 600,
      fontFamily: UI_FONT,
      color: MUTED,
    },
    tableHead: {
      padding: '12px 16px',
      textAlign: 'left',
      fontSize: 11,
      fontWeight: 800,
      fontFamily: UI_FONT,
      color: MUTED,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
      background: '#f8fafc',
    },
    tableCell: {
      padding: '12px 16px',
      fontSize: 14,
      fontFamily: UI_FONT,
      verticalAlign: 'middle',
      color: HEADING,
    },
  },
};

export const getSearchInputStyle = (width = 280) => ({
  width,
  maxWidth: '100%',
  padding: '11px 14px',
  fontSize: 14,
  fontFamily: PAGE.fontFamily,
  border: `1px solid ${PAGE.colors.line}`,
  borderRadius: PAGE.radius.input,
  outline: 'none',
  background: PAGE.colors.bg,
  color: PAGE.colors.text,
  boxSizing: 'border-box',
});

export const getDateInputStyle = () => ({
  padding: '10px 12px',
  fontSize: 13,
  fontFamily: PAGE.fontFamily,
  borderRadius: PAGE.radius.input,
  border: `1px solid ${PAGE.colors.line}`,
  background: PAGE.colors.surface,
  color: PAGE.colors.text,
});

export const getCardStyle = () => ({
  background: PAGE.colors.surface,
  border: `1px solid ${PAGE.colors.line}`,
  borderRadius: PAGE.radius.card,
  boxShadow: '0 4px 16px rgba(15, 23, 42, 0.04)',
});

export const getToolbarStyle = () => ({
  ...getCardStyle(),
  padding: '16px 20px',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 12,
});

export const getDashboardCardStyle = (extra = {}) => ({
  ...getCardStyle(),
  overflow: 'hidden',
  marginBottom: PAGE.sectionGap,
  ...extra,
});

export const getSectionHeaderStyle = () => ({
  padding: '20px 24px',
  borderBottom: `1px solid ${PAGE.colors.line}`,
});

export const getZebraRowStyle = (index) => ({
  background: index % 2 === 0 ? PAGE.colors.surface : PAGE.colors.tableStripe,
  borderBottom: `1px solid ${PAGE.colors.line}`,
});
